import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────
const COLUMNS = [
  { key:'created',     label:'Created',     color:'#976793', borderColor:'#BFDBFE' },
  { key:'in_progress', label:'In Progress', color:'#F59E0B', borderColor:'#FDE68A' },
  { key:'completed',   label:'Completed',   color:'#10B981', borderColor:'#A7F3D0' },
  { key:'on_hold',     label:'On Hold',     color:'#F97316', borderColor:'#FED7AA' },
  { key:'discarded',   label:'Discarded',   color:'#EF4444', borderColor:'#FECACA' },
];

// ── Drag rules: which columns can a card be dropped INTO from a given column
const DROP_RULES = {
  created:     ['in_progress','completed','on_hold','discarded'],
  in_progress: ['completed','on_hold','discarded'],
  completed:   ['on_hold'],
  on_hold:     ['in_progress','completed','discarded'],
  discarded:   [],
};

const TYPE_TABS = ['All','Task','Snags','Hindrance','Followup'];
const fmt = (d) => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:false}) : '—';
const fmtShort = (d) => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:false}) : '—';
const isOverdue = (d,s) => d && new Date(d) < new Date() && s !== 'completed';
const getInitials = (n='') => n.split(' ').map(x=>x[0]).join('').slice(0,2).toUpperCase();

// ── Task Card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, onDragStart, onSelect, isSelected }) {
  const overdue = isOverdue(task.dueDate, task.status);
  const assignee = Array.isArray(task.assignedTo) ? task.assignedTo[0] : task.assignedTo;
  const col = COLUMNS.find(c=>c.key===task.status);

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, task)}
      onClick={() => onSelect(task)}
      style={{
        background:'#fff', border:`1px solid ${isSelected?'#976793':'#E5E7EB'}`,
        borderRadius:10, padding:'12px 14px', cursor:'grab',
        boxShadow: isSelected ? '0 0 0 2px rgba(79,142,247,0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
        transition:'box-shadow 0.15s, transform 0.1s', userSelect:'none',
      }}
      onMouseEnter={e=>{ if(!isSelected) e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e=>{ if(!isSelected) e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)'; }}
    >
      <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:6,marginBottom:5 }}>
        <span style={{ fontSize:13,fontWeight:600,color:'#1F2937',lineHeight:1.35,flex:1 }}>{task.title}</span>
        <div style={{ display:'flex',gap:4,flexShrink:0 }}>
          {task.taskType && <span style={{ background:'#F7F1F7',color:'#976793',padding:'1px 7px',borderRadius:20,fontSize:9,fontWeight:700,textTransform:'uppercase' }}>{task.taskType||'Task'}</span>}
          {overdue && <span style={{ background:'#FEE2E2',color:'#EF4444',padding:'1px 7px',borderRadius:20,fontSize:9,fontWeight:700 }}>Overdue</span>}
        </div>
      </div>
      {task.project && (
        <div style={{ fontSize:11,color:'#976793',marginBottom:8,cursor:'pointer' }}>
          {task.project.title||task.project} {task.project._id && <span style={{ color:'#9CA3AF' }}>({`P-${task.project._id.slice(-4)}`})</span>}
        </div>
      )}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px 10px',marginBottom:8 }}>
        {[
          ['Client', task.project?.client?.name || task.client?.name || '—'],
          ['Assignee', assignee?.name ? assignee.name.slice(0,14)+(assignee.name.length>14?'…':'') : '—'],
          ['Created date', fmtShort(task.createdAt)],
          ['Due date', task.dueDate ? <span style={{ color:overdue?'#EF4444':'inherit' }}>{fmtShort(task.dueDate)}</span> : '—'],
        ].map(([label,val])=>(
          <div key={label}>
            <div style={{ fontSize:9,textTransform:'uppercase',letterSpacing:'0.04em',color:'#9CA3AF',fontWeight:500 }}>{label}</div>
            <div style={{ fontSize:11,color:'#374151' }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid #F3F4F6' }}>
        <span style={{ display:'flex',alignItems:'center',gap:4,fontSize:11,color:'#6B7280',fontVariantNumeric:'tabular-nums' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          {task.actualHours ? `${String(Math.floor(task.actualHours)).padStart(2,'0')}:00:00` : '00:00:00'}
        </span>
        <button style={{ background:'none',border:'none',color:'#9CA3AF',cursor:'pointer',padding:2 }} onClick={e=>e.stopPropagation()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </button>
      </div>
    </div>
  );
}

// ── Task Detail Panel ─────────────────────────────────────────────────────────
function TaskDetailPanel({ task, onClose, onStatusChange, onCommentAdd, isOverlay=false }) {
  const [comment, setComment] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const overdue = isOverdue(task.dueDate, task.status);
  const assignee = Array.isArray(task.assignedTo) ? task.assignedTo[0] : task.assignedTo;
  const col = COLUMNS.find(c=>c.key===task.status);

  useEffect(() => {
    if (!showMenu) return;
    const close = () => setShowMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showMenu]);

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    await onCommentAdd(task._id, comment);
    setComment('');
    setSaving(false);
  };

  const panelStyle = isOverlay ? {
    position:'fixed', top:0, right:0, width:480, height:'100vh',
    background:'#fff', borderLeft:'1px solid #E5E7EB',
    boxShadow:'-4px 0 24px rgba(0,0,0,0.1)', overflowY:'auto',
    zIndex:50, animation:'slideIn 0.2s ease',
  } : {
    flex:1, overflowY:'auto', background:'#fff', borderLeft:'1px solid #E5E7EB',
  };

  return (
    <div style={panelStyle}>
      {isOverlay && (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #E5E7EB',position:'sticky',top:0,background:'#fff',zIndex:1 }}>
          <span style={{ fontSize:14,fontWeight:700,color:'#1F2937' }}>Task Details</span>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
      )}

      <div style={{ padding:'16px 20px' }}>
        {/* Title row */}
        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:6 }}>
          <div>
            <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4 }}>
              <span style={{ fontSize:15,fontWeight:700,color:'#1F2937',lineHeight:1.3 }}>{task.title}</span>
              <span style={{ background:'#F7F1F7',color:'#976793',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>{task.taskType||'Task'}</span>
              {overdue && <span style={{ background:'#FEE2E2',color:'#EF4444',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700 }}>Overdue</span>}
            </div>
            {task.project && (
              <span style={{ fontSize:12,color:'#976793',cursor:'pointer' }}>
                {task.project.title||task.project}
              </span>
            )}
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:6,flexShrink:0 }}>
            {task.status !== 'completed' && (
              <button onClick={()=>onStatusChange(task._id,'completed')}
                style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',color:'#374151',whiteSpace:'nowrap' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Mark Completed
              </button>
            )}
            {/* Approve button for Requests */}
            {task.taskType === 'Request' && (
              <button style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',border:'1px solid #10B981',borderRadius:8,background:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',color:'#10B981' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Approve
              </button>
            )}
            {/* 3-dot menu */}
            <div style={{ position:'relative' }}>
              <button onClick={e=>{e.stopPropagation();setShowMenu(m=>!m);}}
                style={{ width:30,height:30,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF' }}>⋮</button>
              {showMenu && (
                <div style={{ position:'absolute',right:0,top:34,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:200,minWidth:150,overflow:'hidden' }}
                  onClick={e=>e.stopPropagation()}>
                  {['Edit Task','Duplicate','Delete Task','Copy Link'].map(action=>(
                    <button key={action}
                      style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color:action==='Delete Task'?'#EF4444':'#374151' }}
                      onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      {action}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!isOverlay && (
              <button onClick={onClose} style={{ width:30,height:30,border:'none',background:'transparent',cursor:'pointer',color:'#9CA3AF',display:'flex',alignItems:'center',justifyContent:'center' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
        </div>

        {/* Meta grid */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:1,background:'#E5E7EB',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden',marginBottom:16,marginTop:12 }}>
          {[
            ['Stage', <select value={task.status} onChange={e=>onStatusChange(task._id,e.target.value)} style={{ border:'none',background:'transparent',fontSize:12,outline:'none',fontFamily:'inherit',cursor:'pointer',color:col?.color||'#374151' }}>
              {COLUMNS.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
            </select>],
            ['Assignee', <span style={{ fontWeight:600 }}>{assignee?.name||'—'}</span>],
            ['Due Date', <span style={{ color:overdue?'#EF4444':'#374151' }}>{fmt(task.dueDate)}</span>],
            ['Time Spent', '00:00:00'],
            ['Client Name', task.project?.client?.name||task.client?.name||'—'],
            ['Created', fmt(task.createdAt)],
            ['Created By', task.createdBy?.name||'—'],
            ['Completed', task.completedAt ? fmt(task.completedAt) : 'N/A'],
          ].map(([label,val])=>(
            <div key={label} style={{ background:'#fff',padding:'10px 12px' }}>
              <div style={{ fontSize:10,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:500,marginBottom:3 }}>{label}</div>
              <div style={{ fontSize:12,color:'#374151' }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Description */}
        {task.description && (
          <div style={{ background:'#F9FAFB',border:'1px solid #F3F4F6',borderRadius:10,padding:'12px 14px',marginBottom:16 }}>
            <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:8 }}>Description</div>
            <div style={{ fontSize:13,color:'#6B7280',lineHeight:1.7,whiteSpace:'pre-wrap' }}>{task.description}</div>
          </div>
        )}

        {/* Comments */}
        <div style={{ border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden' }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB' }}>
            <span style={{ fontSize:13,fontWeight:600,color:'#374151' }}>Comments ({task.comments?.length||0})</span>
            <button onClick={()=>setShowComments(s=>!s)} style={{ background:'none',border:'none',fontSize:12,color:'#976793',cursor:'pointer' }}>
              {showComments ? 'Hide' : 'Show'}
            </button>
          </div>
          {showComments && (
            <>
              {/* Comment input */}
              <div style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <input value={comment} onChange={e=>setComment(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleComment()}
                  placeholder="Write a comment…" style={{ flex:1,border:'none',background:'transparent',outline:'none',fontSize:13,fontFamily:'inherit',color:'#374151' }} />
                <div style={{ width:30,height:30,borderRadius:'50%',background:'#6366F1',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/></svg>
                </div>
                <span style={{ fontSize:10,color:'#9CA3AF' }}>00:00</span>
              </div>
              {/* Comments list */}
              {(!task.comments || task.comments.length === 0) ? (
                <div style={{ textAlign:'center',padding:'30px',color:'#9CA3AF' }}>
                  <svg width="48" height="48" viewBox="0 0 100 80" fill="none" style={{ margin:'0 auto 8px',display:'block' }}>
                    <rect x="20" y="10" width="60" height="45" rx="4" fill="#F3F4F6"/>
                    <rect x="30" y="20" width="40" height="5" rx="2" fill="#D1D5DB"/>
                    <rect x="30" y="30" width="30" height="5" rx="2" fill="#D1D5DB"/>
                    <polygon points="35,55 45,55 40,65" fill="#F3F4F6"/>
                  </svg>
                  <p style={{ fontSize:12 }}>No comments have been made to this task yet</p>
                </div>
              ) : task.comments.map((c,i)=>(
                <div key={i} style={{ display:'flex',gap:10,padding:'10px 14px',borderBottom:'1px solid #F9FAFB' }}>
                  <div style={{ width:28,height:28,borderRadius:'50%',background:'#976793',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                    {getInitials(c.user?.name||'U')}
                  </div>
                  <div>
                    <span style={{ fontSize:12,fontWeight:600,color:'#374151' }}>{c.user?.name||'User'}</span>
                    <p style={{ fontSize:12,color:'#6B7280',margin:'3px 0 2px' }}>{c.text}</p>
                    <span style={{ fontSize:10,color:'#9CA3AF' }}>{fmt(c.createdAt)}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}

// ── Filter Panel ──────────────────────────────────────────────────────────────
function FilterPanel({ filters, setFilters, projects, clients, onClear, onClose }) {
  const [local, setLocal] = useState({ ...filters });
  const h = (k,v) => setLocal(f=>({...f,[k]:v}));
  const iS = { width:'100%',padding:'7px 10px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#F9FAFB',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4 };

  return (
    <div style={{ position:'absolute',top:42,right:0,background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,boxShadow:'0 8px 32px rgba(0,0,0,0.12)',zIndex:200,minWidth:280,padding:0,overflow:'hidden' }}
      onClick={e=>e.stopPropagation()}>
      <div style={{ padding:'10px 14px',borderBottom:'1px solid #E5E7EB' }}>
        {[
          { label:'Created By', key:'createdBy', type:'text' },
          { label:'Assigned To', key:'assignedTo', type:'text' },
          { label:'Project', key:'project', type:'text' },
          { label:'Client', key:'client', type:'text' },
          { label:'Stage', key:'stage', type:'select', opts: COLUMNS.map(c=>c.label) },
          { label:'Priority', key:'priority', type:'select', opts:['Low','Medium','High','Urgent'] },
          { label:'Due Date', key:'dueDate', type:'date' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom:10 }}>
            <label style={lS}>{f.label}</label>
            {f.type === 'select' ? (
              <select style={iS} value={local[f.key]||''} onChange={e=>h(f.key,e.target.value)}>
                <option value="">All</option>
                {f.opts.map(o=><option key={o}>{o}</option>)}
              </select>
            ) : (
              <input type={f.type} style={iS} value={local[f.key]||''} onChange={e=>h(f.key,e.target.value)} />
            )}
          </div>
        ))}
      </div>
      <div style={{ padding:'8px 14px',display:'flex',justifyContent:'flex-end' }}>
        <button onClick={()=>{ setFilters({}); onClear(); onClose(); }}
          style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,cursor:'pointer',color:'#374151' }}>Clear</button>
      </div>
    </div>
  );
}

// ── Add Task Modal ────────────────────────────────────────────────────────────
function AddTaskModal({ projects, onClose, onSave }) {
  const [form, setForm] = useState({ title:'', project:'', priority:'medium', dueDate:'', description:'', status:'created', taskType:'Task' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const h = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const iS = { width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,background:'#F9FAFB',fontSize:13,outline:'none',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',color:'#6B7280',marginBottom:4 };

  const submit = async e => {
    e.preventDefault(); setError(''); setSaving(true);
    try { await onSave(form); onClose(); }
    catch(err) { setError(err.response?.data?.message||'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:500,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'18px 24px 0' }}>
          <h3 style={{ margin:0,fontSize:18,fontWeight:700,color:'#1F2937' }}>Add Task</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <div style={{ padding:'16px 24px' }}>
          {error && <div style={{ background:'#FEE2E2',border:'1px solid #FECACA',color:'#EF4444',padding:'10px',borderRadius:8,fontSize:13,marginBottom:12 }}>{error}</div>}
          <form id="task-form" onSubmit={submit}>
            <div style={{ marginBottom:12 }}><label style={lS}>Task Title *</label><input style={iS} name="title" value={form.title} onChange={h} required /></div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
              <div><label style={lS}>Project *</label>
                <select style={iS} name="project" value={form.project} onChange={h} required>
                  <option value="">Select…</option>
                  {projects.map(p=><option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
              </div>
              <div><label style={lS}>Type</label>
                <select style={iS} name="taskType" value={form.taskType} onChange={h}>
                  {['Task','Snags','Hindrance','Followup','Request'].map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
              <div><label style={lS}>Priority</label>
                <select style={iS} name="priority" value={form.priority} onChange={h}>
                  {['low','medium','high','urgent'].map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label style={lS}>Due Date</label><input type="date" style={iS} name="dueDate" value={form.dueDate} onChange={h} /></div>
            </div>
            <div style={{ marginBottom:12 }}><label style={lS}>Description</label>
              <textarea style={{...iS,minHeight:70,resize:'vertical'}} name="description" value={form.description} onChange={h} />
            </div>
          </form>
        </div>
        <div style={{ padding:'0 24px 20px',display:'flex',gap:10,justifyContent:'flex-end',borderTop:'1px solid #F3F4F6',paddingTop:14 }}>
          <button onClick={onClose} style={{ padding:'8px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer',color:'#374151' }}>Cancel</button>
          <button type="submit" form="task-form" disabled={saving}
            style={{ padding:'8px 20px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            {saving ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Tasks Page ───────────────────────────────────────────────────────────
export default function Tasks() {
  const { user } = useAuth();
  const [columns, setColumns]       = useState({});
  const [allTasks, setAllTasks]     = useState([]);
  const [projects, setProjects]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [typeTab, setTypeTab]       = useState('All');
  const [mainTab, setMainTab]       = useState('tasks'); // 'tasks' | 'requests'
  const [viewMode, setViewMode]     = useState('kanban');
  const [showModal, setShowModal]   = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters]       = useState({});

  const dragTask  = useRef(null);
  const dragFrom  = useRef(null);

  const buildCols = useCallback((tasks) => {
    const cols = {};
    COLUMNS.forEach(c => cols[c.key] = []);
    tasks.forEach(t => {
      const k = t.status || 'created';
      if (cols[k]) cols[k].push(t);
    });
    setColumns(cols);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, pr] = await Promise.all([
        api.get('/tasks', { params:{ limit:200 } }),
        api.get('/projects', { params:{ limit:100 } }),
      ]);
      const tasks = tr.data.data.tasks || [];
      setAllTasks(tasks);
      buildCols(tasks);
      setProjects(pr.data.data.projects || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [buildCols]);

  useEffect(() => { load(); }, [load]);

  // Close filter on outside click
  useEffect(() => {
    if (!showFilterPanel) return;
    const close = (e) => setShowFilterPanel(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showFilterPanel]);

  // ── Drag & Drop with rules ───────────────────────────────────────────────
  const handleDragStart = (e, task) => {
    dragTask.current = task;
    dragFrom.current = task.status;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, toKey) => {
    e.preventDefault();
    const task = dragTask.current;
    const from = dragFrom.current;
    if (!task || from === toKey) return;

    // Check drag rules
    const allowed = DROP_RULES[from] || [];
    if (!allowed.includes(toKey)) {
      // Show brief shake feedback (noop for now, just return)
      return;
    }

    // Optimistic update
    setColumns(prev => {
      const next = { ...prev };
      next[from] = (next[from]||[]).filter(t => t._id !== task._id);
      next[toKey] = [...(next[toKey]||[]), { ...task, status: toKey }];
      return next;
    });
    if (selectedTask?._id === task._id) {
      setSelectedTask(t => ({ ...t, status: toKey }));
    }

    try {
      await api.patch(`/tasks/${task._id}/status`, { status: toKey });
    } catch { load(); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    const task = allTasks.find(t=>t._id===taskId) || selectedTask;
    const from = task?.status;
    if (from) {
      const allowed = DROP_RULES[from] || [];
      if (!allowed.includes(newStatus) && newStatus !== from) {
        alert(`Cannot move task from "${COLUMNS.find(c=>c.key===from)?.label}" to "${COLUMNS.find(c=>c.key===newStatus)?.label}"`);
        return;
      }
    }
    setColumns(prev => {
      const next = { ...prev };
      let found;
      COLUMNS.forEach(col => {
        const idx = (next[col.key]||[]).findIndex(t=>t._id===taskId);
        if (idx !== -1) { found = next[col.key][idx]; next[col.key] = next[col.key].filter(t=>t._id!==taskId); }
      });
      if (found) next[newStatus] = [...(next[newStatus]||[]), { ...found, status:newStatus }];
      return next;
    });
    if (selectedTask?._id === taskId) setSelectedTask(t=>({...t,status:newStatus}));
    try { await api.patch(`/tasks/${taskId}/status`, { status:newStatus }); } catch { load(); }
  };

  const handleCommentAdd = async (taskId, text) => {
    await api.post(`/tasks/${taskId}/comments`, { text });
    // Update local
    const updater = t => t._id === taskId ? { ...t, comments:[...(t.comments||[]),{text,createdAt:new Date(),user:{name:user?.name}}] } : t;
    setAllTasks(prev => prev.map(updater));
    setColumns(prev => {
      const next = { ...prev };
      COLUMNS.forEach(col => { next[col.key] = (next[col.key]||[]).map(updater); });
      return next;
    });
    if (selectedTask?._id === taskId) setSelectedTask(t=>({...t,comments:[...(t.comments||[]),{text,createdAt:new Date(),user:{name:user?.name}}]}));
  };

  const handleSaveTask = async (form) => { await api.post('/tasks', form); load(); };

  // Filter tasks for list view
  const filterTasks = useCallback((tasks) => {
    return tasks.filter(t => {
      const matchSearch = !search || t.title?.toLowerCase().includes(search.toLowerCase());
      const matchType   = typeTab === 'All' || (t.taskType||'Task') === typeTab;
      return matchSearch && matchType;
    });
  }, [search, typeTab]);

  const totalCount  = Object.values(columns).reduce((a,c)=>a+c.length,0);
  const requestTasks = allTasks.filter(t => t.taskType === 'Request');

  if (loading) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',flexDirection:'column',gap:12,color:'#9CA3AF' }}>
      <div style={{ width:32,height:32,border:'3px solid #E5E7EB',borderTopColor:'#976793',borderRadius:'50%',animation:'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ display:'flex',flexDirection:'column',height:'100%',background:'#F8F9FB',fontFamily:'inherit' }}>
      {/* ── Top Bar ── */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',background:'#fff',borderBottom:'1px solid #E5E7EB',flexShrink:0 }}>
        <div style={{ display:'flex',gap:4 }}>
          <button onClick={()=>setMainTab('tasks')}
            style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:500,cursor:'pointer',border:'none',
              background:mainTab==='tasks'?'#F7F1F7':'transparent', color:mainTab==='tasks'?'#976793':'#6B7280' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/></svg>
            All Tasks ({totalCount})
          </button>
          <button onClick={()=>setMainTab('requests')}
            style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:500,cursor:'pointer',border:'none',
              background:mainTab==='requests'?'#F7F1F7':'transparent', color:mainTab==='requests'?'#976793':'#6B7280' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
            All Requests {requestTasks.length > 0 && `(${requestTasks.length})`}
          </button>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:8 }}>
          {/* Kanban/List view toggle */}
          <button onClick={()=>setViewMode('kanban')} title="Kanban"
            style={{ width:32,height:32,border:'1px solid #E5E7EB',borderRadius:6,background:viewMode==='kanban'?'#F7F1F7':'#fff',color:viewMode==='kanban'?'#976793':'#9CA3AF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/></svg>
          </button>
          <button onClick={()=>{ setViewMode('list'); setSelectedTask(null); }} title="List"
            style={{ width:32,height:32,border:'1px solid #E5E7EB',borderRadius:6,background:viewMode==='list'?'#F7F1F7':'#fff',color:viewMode==='list'?'#976793':'#9CA3AF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
          </button>
          {/* Voice */}
          <div style={{ width:32,height:32,borderRadius:'50%',border:'2px solid #976793',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#976793' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/></svg>
          </div>
          <button onClick={()=>setShowModal(true)}
            style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Task
          </button>
          <button style={{ width:30,height:30,border:'none',background:'transparent',cursor:'pointer',color:'#9CA3AF',display:'flex',alignItems:'center',justifyContent:'center' }}>⋮</button>
        </div>
      </div>

      {/* ── Type Tabs ── */}
      {mainTab === 'tasks' && (
        <div style={{ display:'flex',padding:'0 20px',background:'#fff',borderBottom:'1px solid #E5E7EB',flexShrink:0 }}>
          {TYPE_TABS.map(t => (
            <button key={t} onClick={()=>setTypeTab(t)}
              style={{ padding:'9px 14px',fontSize:13,fontWeight:typeTab===t?600:400,color:typeTab===t?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:typeTab===t?'2px solid #976793':'2px solid transparent',cursor:'pointer',whiteSpace:'nowrap',marginBottom:-1,transition:'all 0.15s' }}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* ── Search + Filter ── */}
      <div style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 20px',background:'#fff',borderBottom:'1px solid #E5E7EB',flexShrink:0 }}>
        <div style={{ position:'relative',flex:1,maxWidth:300 }}>
          <svg style={{ position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'#9CA3AF',pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input placeholder="Search tasks…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%',padding:'7px 10px 7px 30px',border:'1px solid #E5E7EB',borderRadius:8,background:'#F9FAFB',fontSize:13,outline:'none',fontFamily:'inherit' }} />
        </div>
        <div style={{ position:'relative',marginLeft:'auto' }}>
          <button onClick={e=>{e.stopPropagation();setShowFilterPanel(s=>!s);}}
            style={{ width:34,height:34,borderRadius:'50%',border:'1.5px solid #E5E7EB',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color: Object.keys(filters).length>0?'#976793':'#6B7280' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          </button>
          {showFilterPanel && (
            <FilterPanel filters={filters} setFilters={setFilters} projects={projects} clients={[]}
              onClear={()=>setFilters({})} onClose={()=>setShowFilterPanel(false)} />
          )}
        </div>
      </div>

      {/* ── KANBAN BOARD ── */}
      {viewMode === 'kanban' && mainTab === 'tasks' && (
        <div style={{ flex:1,display:'flex',overflowX:'auto',overflowY:'hidden',padding:'16px',gap:0,background:'#F8F9FB' }}>
          {COLUMNS.map(col => {
            const colTasks = filterTasks(columns[col.key]||[]);
            const canReceive = Object.entries(DROP_RULES).some(([from,targets]) => targets.includes(col.key));
            return (
              <div key={col.key}
                style={{ flex:'0 0 260px',display:'flex',flexDirection:'column',borderRight:'1px solid #E8EAF0',padding:'0 8px',height:'100%',minHeight:300 }}
                onDragOver={e=>{ e.preventDefault(); e.dataTransfer.dropEffect = canReceive ? 'move' : 'none'; }}
                onDrop={e=>handleDrop(e,col.key)}
              >
                {/* Column header */}
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 8px',borderTop:`3px solid ${col.color}`,marginBottom:10,background:'#fff',borderBottom:'1px solid #E5E7EB' }}>
                  <span style={{ fontSize:13,fontWeight:700,color:'#374151' }}>{col.label}</span>
                  <span style={{ background:'#F3F4F6',color:'#6B7280',borderRadius:20,padding:'1px 8px',fontSize:11,fontWeight:600 }}>{colTasks.length}</span>
                </div>
                {/* Cards */}
                <div style={{ flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:8,paddingBottom:8 }}>
                  {colTasks.length === 0 ? (
                    <div style={{ textAlign:'center',padding:'40px 0',color:'#D1D5DB',fontSize:12 }}>No tasks</div>
                  ) : colTasks.map(task => (
                    <TaskCard key={task._id} task={task} onDragStart={handleDragStart}
                      onSelect={setSelectedTask} isSelected={selectedTask?._id===task._id} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── LIST VIEW (All Tasks / All Requests) ── */}
      {viewMode === 'list' && (
        <div style={{ flex:1,display:'flex',overflow:'hidden' }}>
          {/* Left list */}
          <div style={{ width:440,overflowY:'auto',borderRight:'1px solid #E5E7EB',background:'#fff',flexShrink:0 }}>
            {filterTasks(mainTab==='requests' ? requestTasks : allTasks).map((task,idx,arr) => {
              const overdue = isOverdue(task.dueDate,task.status);
              const assignee = Array.isArray(task.assignedTo)?task.assignedTo[0]:task.assignedTo;
              const col = COLUMNS.find(c=>c.key===task.status);
              return (
                <div key={task._id}
                  onClick={()=>setSelectedTask(task)}
                  style={{ padding:'12px 14px',borderBottom:'1px solid #F3F4F6',cursor:'pointer',background:selectedTask?._id===task._id?'#F7F1F7':'#fff',borderLeft:selectedTask?._id===task._id?'3px solid #976793':'3px solid transparent',transition:'background 0.1s' }}
                  onMouseEnter={e=>{ if(selectedTask?._id!==task._id) e.currentTarget.style.background='#F9FAFB'; }}
                  onMouseLeave={e=>{ if(selectedTask?._id!==task._id) e.currentTarget.style.background='#fff'; }}>
                  <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:6,marginBottom:4 }}>
                    <span style={{ fontSize:13,fontWeight:600,color:'#1F2937',lineHeight:1.3,flex:1 }}>{task.title}</span>
                    <div style={{ display:'flex',gap:4,flexShrink:0 }}>
                      {task.taskType && <span style={{ background:'#F7F1F7',color:'#976793',padding:'1px 6px',borderRadius:20,fontSize:9,fontWeight:700 }}>{task.taskType||'Task'}</span>}
                      {overdue && <span style={{ background:'#FEE2E2',color:'#EF4444',padding:'1px 6px',borderRadius:20,fontSize:9,fontWeight:700 }}>Overdue</span>}
                    </div>
                  </div>
                  {assignee && (
                    <div style={{ display:'flex',alignItems:'center',gap:5,marginBottom:5 }}>
                      <div style={{ width:18,height:18,borderRadius:'50%',background:'#976793',color:'#fff',fontSize:8,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center' }}>
                        {getInitials(assignee.name)}
                      </div>
                      <span style={{ fontSize:11,color:'#374151',fontWeight:500 }}>{assignee.name}</span>
                    </div>
                  )}
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:11,color:'#6B7280' }}>
                    <span>Completed On<br/><span style={{ color:'#374151' }}>{task.completedAt?fmt(task.completedAt):'N/A'}</span></span>
                    <div style={{ textAlign:'right' }}>
                      <div>Due Date</div>
                      <div style={{ color:overdue?'#EF4444':'#374151' }}>{task.dueDate?fmt(task.dueDate):'N/A'}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:6 }}>
                    <span style={{ fontSize:11,color:'#6B7280',display:'flex',alignItems:'center',gap:4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                      00:00:00
                    </span>
                    <span style={{ background:col?.color?`${col.color}20`:'#F3F4F6',color:col?.color||'#6B7280',padding:'2px 8px',borderRadius:20,fontSize:9,fontWeight:700 }}>
                      {col?.label||task.status}
                    </span>
                  </div>
                  {task.project && <div style={{ fontSize:10,color:'#976793',marginTop:4 }}>{task.project.title||task.project}</div>}
                </div>
              );
            })}
            <div style={{ textAlign:'center',padding:'12px',fontSize:11,color:'#9CA3AF' }}>
              All tasks loaded ({filterTasks(mainTab==='requests'?requestTasks:allTasks).length} of {mainTab==='requests'?requestTasks.length:allTasks.length})
            </div>
          </div>

          {/* Right detail */}
          <div style={{ flex:1,overflowY:'auto',background:'#fff' }}>
            {selectedTask ? (
              <TaskDetailPanel task={selectedTask} onClose={()=>setSelectedTask(null)}
                onStatusChange={handleStatusChange} onCommentAdd={handleCommentAdd} isOverlay={false} />
            ) : (
              <div style={{ display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#9CA3AF',gap:12 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
                <p style={{ fontSize:14,fontWeight:500 }}>Select a task to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Kanban with slide-over detail (clicking card in kanban) ── */}
      {viewMode === 'kanban' && selectedTask && (
        <TaskDetailPanel task={selectedTask} onClose={()=>setSelectedTask(null)}
          onStatusChange={handleStatusChange} onCommentAdd={handleCommentAdd} isOverlay={true} />
      )}

      {showModal && <AddTaskModal projects={projects} onClose={()=>setShowModal(false)} onSave={handleSaveTask} />}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}